import { useEffect, useRef, useState } from 'react';
import {
    User,
    Calendar,
    Mail,
    Phone,
    MapPin,
    Shield,
    CheckCircle,
    XCircle,
    Gift,
    Loader2,
    QrCode,
    Copy,
    Check,
    ExternalLink,
    Download,
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User as UserType, UserRole, UserStatus, VERIFIABLE_ROLES } from '@/types';
import { useSetPaymentExemptionMutation } from '@/store/api/apiSlice';
import { parseErrorMessage } from '@/utils/parseErrorMessage';

interface UserDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: UserType | null;
}

export default function UserDetailsModal({ isOpen, onClose, user }: UserDetailsModalProps) {
    // Local override so the toggle reflects instantly (the `user` prop is a snapshot
    // from the list; the list itself refetches via tag invalidation).
    const [exemptOverride, setExemptOverride] = useState<boolean | null>(null);
    const [billingError, setBillingError] = useState('');
    const [linkCopied, setLinkCopied] = useState(false);
    const [setExemption, { isLoading: isTogglingExempt }] = useSetPaymentExemptionMutation();
    const qrWrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setExemptOverride(null);
        setBillingError('');
        setLinkCopied(false);
    }, [user?.id]);

    if (!user) return null;

    // Field members (agent / medical / notary) carry a public verification QR.
    // The QR encodes the link to the public trust card, which the backend serves
    // from GET /verify/:userId. Non-field roles 404 there, so we don't offer it.
    const isVerifiable = VERIFIABLE_ROLES.includes(user.role);
    const verifyUrl = `${window.location.origin}/verify/${user.id}`;

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(verifyUrl);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch {
            setLinkCopied(false);
        }
    };

    const handleDownloadQr = () => {
        const canvas = qrWrapRef.current?.querySelector('canvas');
        if (!canvas) return;
        const link = document.createElement('a');
        link.download = `yourchecka-verify-${user.firstName}-${user.lastName}.png`.toLowerCase().replace(/\s+/g, '-');
        link.href = canvas.toDataURL('image/png');
        link.click();
    };

    const isExempt = exemptOverride ?? user.paymentExempt ?? false;

    const handleToggleExemption = async () => {
        setBillingError('');
        try {
            const updated = await setExemption({ id: user.id, data: { exempt: !isExempt } }).unwrap();
            setExemptOverride(updated.paymentExempt ?? !isExempt);
        } catch (err) {
            setBillingError(parseErrorMessage(err, 'Failed to update payment exemption.'));
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const formatDateTime = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const getStatusColor = (status: UserStatus) => {
        switch (status) {
            case UserStatus.ACTIVE:
                return 'bg-green-50 text-green-700 border-green-100';
            case UserStatus.INACTIVE:
                return 'bg-gray-50 text-gray-700 border-gray-100';
            case UserStatus.SUSPENDED:
                return 'bg-red-50 text-red-700 border-red-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const getRoleColor = (role: UserRole) => {
        switch (role) {
            case UserRole.EMPLOYER:
                return 'bg-blue-50 text-blue-700 border-blue-100';
            case UserRole.AGENT:
                return 'bg-purple-50 text-purple-700 border-purple-100';
            case UserRole.SUPER_ADMIN:
                return 'bg-orange-50 text-orange-700 border-orange-100';
            default:
                return 'bg-gray-50 text-gray-700 border-gray-100';
        }
    };

    const formatRole = (role: UserRole) => {
        switch (role) {
            case UserRole.EMPLOYER:
                return 'Employer';
            case UserRole.AGENT:
                return 'Agent';
            case UserRole.SUPER_ADMIN:
                return 'Super Admin';
            default:
                return role;
        }
    };

    const getInitials = (firstName?: string, lastName?: string) => {
        const first = firstName?.charAt(0) || '';
        const last = lastName?.charAt(0) || '';
        return (first + last).toUpperCase() || 'U';
    };

    return (
        <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto">
                <div className="py-6">
                    {/* Header */}
                    <div className="mb-6">
                        <div className="flex items-center justify-between mb-2">
                            <h2 className="text-2xl font-bold text-gray-900">User Details</h2>
                            <Badge
                                variant="outline"
                                className={`${getStatusColor(user.status)} px-3 py-1 rounded-lg font-medium capitalize`}
                            >
                                {user.status.toLowerCase()}
                            </Badge>
                        </div>
                        <p className="text-sm text-gray-600">View and manage user information</p>
                    </div>

                    {/* User Profile Card */}
                    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 mb-6 text-white">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                                <span className="text-2xl font-bold">{getInitials(user.firstName, user.lastName)}</span>
                            </div>
                            <div>
                                <h3 className="text-xl font-bold">
                                    {user.firstName} {user.lastName}
                                </h3>
                                <Badge variant="outline" className={`${getRoleColor(user.role)} mt-1`}>
                                    {formatRole(user.role)}
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Verification QR & profile link — field members only */}
                    {isVerifiable && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <QrCode className="w-4 h-4 text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                    Public Verification
                                </h3>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                                    <div
                                        ref={qrWrapRef}
                                        className="bg-white p-2.5 rounded-lg border border-gray-200 shrink-0 self-center sm:self-start"
                                    >
                                        <QRCodeCanvas value={verifyUrl} size={116} level="M" includeMargin={false} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs text-gray-600 mb-2">
                                            Scanning this code opens the member's public trust card. Print it on their
                                            ID card.
                                        </p>
                                        <span className="text-xs text-gray-600">Profile link</span>
                                        <div className="mt-1 flex items-center gap-1.5 bg-white border border-gray-200 rounded-lg px-2.5 py-2">
                                            <span
                                                className="text-xs font-mono text-gray-700 truncate flex-1"
                                                title={verifyUrl}
                                            >
                                                {verifyUrl}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={handleCopyLink}
                                                title="Copy link"
                                                className="shrink-0 text-gray-500 hover:text-gray-900 w-7 h-7 rounded flex items-center justify-center hover:bg-gray-100"
                                            >
                                                {linkCopied ? (
                                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                                ) : (
                                                    <Copy className="w-3.5 h-3.5" />
                                                )}
                                            </button>
                                        </div>
                                        <div className="mt-2.5 flex items-center gap-2">
                                            <a
                                                href={verifyUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-100 px-3 py-1.5 rounded-lg"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5" /> Open
                                            </a>
                                            <button
                                                type="button"
                                                onClick={handleDownloadQr}
                                                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-700 border border-gray-200 bg-white hover:bg-gray-100 px-3 py-1.5 rounded-lg"
                                            >
                                                <Download className="w-3.5 h-3.5" /> Download QR
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Contact Information */}
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <User className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                Contact Information
                            </h3>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <Mail className="w-4 h-4 text-gray-400" />
                                <div className="flex-1">
                                    <span className="text-xs text-gray-600">Email</span>
                                    <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                </div>
                                <div className="flex items-center gap-1">
                                    {user.isEmailVerified ? (
                                        <>
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                            <span className="text-xs text-green-600">Verified</span>
                                        </>
                                    ) : (
                                        <>
                                            <XCircle className="w-4 h-4 text-red-600" />
                                            <span className="text-xs text-red-600">Not Verified</span>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <Phone className="w-4 h-4 text-gray-400" />
                                <div className="flex-1">
                                    <span className="text-xs text-gray-600">Phone</span>
                                    <p className="text-sm font-medium text-gray-900">{user.phoneNumber || 'N/A'}</p>
                                </div>
                                {user.phoneNumber && (
                                    <div className="flex items-center gap-1">
                                        {user.isPhoneVerified ? (
                                            <>
                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                                <span className="text-xs text-green-600">Verified</span>
                                            </>
                                        ) : (
                                            <>
                                                <XCircle className="w-4 h-4 text-red-600" />
                                                <span className="text-xs text-red-600">Not Verified</span>
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Additional Information */}
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                Additional Information
                            </h3>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            {user.companyName && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Company</span>
                                    <span className="text-sm font-medium text-gray-900">{user.companyName}</span>
                                </div>
                            )}
                            {user.gender && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Gender</span>
                                    <span className="text-sm font-medium text-gray-900 capitalize">{user.gender}</span>
                                </div>
                            )}
                            {user.country && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">Country</span>
                                    <span className="text-sm font-medium text-gray-900">{user.country}</span>
                                </div>
                            )}
                            {user.state && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">State</span>
                                    <span className="text-sm font-medium text-gray-900">{user.state}</span>
                                </div>
                            )}
                            {user.city && (
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">City</span>
                                    <span className="text-sm font-medium text-gray-900">{user.city}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Billing — payment exemption (employer accounts only) */}
                    {user.role === UserRole.EMPLOYER && (
                        <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                                <Gift className="w-4 h-4 text-gray-500" />
                                <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Billing</h3>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-medium text-gray-900">Payment exemption</span>
                                            <Badge
                                                variant="outline"
                                                className={
                                                    isExempt
                                                        ? 'bg-green-50 text-green-700 border-green-100'
                                                        : 'bg-gray-100 text-gray-600 border-gray-200'
                                                }
                                            >
                                                {isExempt ? 'Complimentary' : 'Pays normally'}
                                            </Badge>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {isExempt
                                                ? 'Investigations are created free — payment is bypassed and the employee is invited immediately.'
                                                : 'This employer pays for every investigation as usual.'}
                                        </p>
                                    </div>
                                    <Button
                                        onClick={handleToggleExemption}
                                        disabled={isTogglingExempt}
                                        className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium text-white ${
                                            isExempt
                                                ? 'bg-gray-900 hover:bg-gray-800'
                                                : 'bg-[#FF6B35] hover:bg-[#FF5722]'
                                        }`}
                                    >
                                        {isTogglingExempt ? (
                                            <span className="flex items-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Saving...
                                            </span>
                                        ) : isExempt ? (
                                            'Revert to paying'
                                        ) : (
                                            'Make complimentary'
                                        )}
                                    </Button>
                                </div>
                                {billingError && <p className="text-xs text-red-600 mt-2">{billingError}</p>}
                            </div>
                        </div>
                    )}

                    {/* Account Security */}
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Shield className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                Account Security
                            </h3>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">OTP Authentication</span>
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-100">
                                    Enabled
                                </Badge>
                            </div>
                        </div>
                    </div>

                    {/* Activity Timeline */}
                    <div className="mb-6">
                        <div className="flex items-center gap-2 mb-3">
                            <Calendar className="w-4 h-4 text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                                Activity Timeline
                            </h3>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Date Joined</span>
                                <span className="text-sm font-medium text-gray-900">{formatDate(user.createdAt)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600">Last Updated</span>
                                <span className="text-sm font-medium text-gray-900">
                                    {formatDateTime(user.updatedAt)}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="sticky bottom-0 bg-white pt-4 border-t border-gray-200 flex gap-3">
                        <Button onClick={onClose} variant="outline" className="flex-1">
                            Close
                        </Button>
                        <Button className="flex-1 bg-gray-900 hover:bg-gray-800 text-white">Edit User</Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
